import { observable } from '@legendapp/state';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/lib/supabaseClient';
import { clearFeaturebaseIdentity } from '@/lib/featurebase';
import { clearMobileData } from '@/lib/mobileSync';
import { trackEvent, setOnceUserProperties } from '@/lib/analytics';

const NATIVE_REDIRECT = 'promptnote://auth-callback';

const NOTIFY_ACTIVATION_URL = 'https://tafnybfhwybgijozwfyq.supabase.co/functions/v1/notify-activation';

// In-flight guard: prevents duplicate emails when onAuthStateChange fires
// INITIAL_SESSION + SIGNED_IN back-to-back during signup
let notifyInflight = false;

/**
 * Fire-and-forget activation notification.
 * Idempotent via app_metadata.activation_notified and client-side in-flight guard.
 */
function notifyActivation(session) {
  if (notifyInflight) return;
  try {
    const user = session.user;
    if (user.app_metadata?.activation_notified) return;
    if (user.is_anonymous) return;
    notifyInflight = true;
    fetch(NOTIFY_ACTIVATION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        type: 'user_activation',
        app_id: 'promptnote',
        record: {
          id: user.id,
          email: user.email,
          raw_user_meta_data: user.user_metadata,
          created_at: user.created_at,
        },
      }),
    }).catch((err) => {
      console.warn('[auth-notify] Activation notification failed (non-blocking):', err.message);
    }).finally(() => {
      notifyInflight = false;
    });
  } catch (err) {
    notifyInflight = false;
    console.warn('[auth-notify] notifyActivation error (non-blocking):', err.message);
  }
}

// Auth state using Supabase Auth
export const auth$ = observable({
  user: null,
  session: null,
  isAuthenticated: false,
  isAnonymous: false, // Track if user is anonymous (guest)
  isLoading: true,
});

// Initialize: check existing session
// Use setTimeout to ensure React components have mounted and subscribed
setTimeout(() => {
  supabase.auth.getSession().then(({ data: { session } }) => {
    console.log('[Auth] getSession result:', session ? 'has session' : 'no session');
    if (session) {
      const isAnonymous = session.user.is_anonymous || false;
      auth$.user.set({
        id: session.user.id,
        username: session.user.user_metadata?.username || session.user.email?.split('@')[0] || 'Guest',
        email: session.user.email,
        is_anonymous: isAnonymous,
      });
      auth$.session.set(session);
      auth$.isAuthenticated.set(true);
      auth$.isAnonymous.set(isAnonymous);
      console.log('[Auth] User is anonymous:', isAnonymous);
    }
    auth$.isLoading.set(false);
    console.log('[Auth] After getSession - isLoading:', auth$.isLoading.get(), 'isAuthenticated:', auth$.isAuthenticated.get());
  });
}, 0);

// Listen for auth changes
supabase.auth.onAuthStateChange((event, session) => {
  console.log('[Auth] onAuthStateChange:', event, session ? 'has session' : 'no session');

  if (session) {
    const isAnonymous = session.user.is_anonymous || false;
    auth$.user.set({
      id: session.user.id,
      username: session.user.user_metadata?.username || session.user.email?.split('@')[0] || 'Guest',
      email: session.user.email,
      is_anonymous: isAnonymous,
    });
    auth$.session.set(session);
    auth$.isAuthenticated.set(true);
    auth$.isAnonymous.set(isAnonymous);
    if (!isAnonymous) notifyActivation(session);
    console.log('[Auth] Set authenticated to TRUE, isAnonymous:', isAnonymous);
  } else {
    auth$.user.set(null);
    auth$.session.set(null);
    auth$.isAuthenticated.set(false);
    auth$.isAnonymous.set(false);
    console.log('[Auth] Set authenticated to FALSE');
  }
});

// Sign in anonymously (guest mode)
export const signInAnonymously = async () => {
  console.log('[Auth] Signing in anonymously...');
  const { data, error } = await supabase.auth.signInAnonymously({
    options: { data: { app_id: 'promptnote' } },
  });
  if (error) {
    console.error('[Auth] Anonymous sign-in error:', error);
    throw error;
  }
  console.log('[Auth] Anonymous sign-in successful');
  return data;
};

// Login with email
export const login = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
};

// Register with email and username
export const register = async (email, username, password) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: { username, app_id: 'promptnote' } },
  });
  if (error) throw error;

  trackEvent('signup_completed', { method: 'email' });
  setOnceUserProperties({ signup_at: new Date().toISOString(), signup_method: 'email' });

  return data;
};

// Logout - sign out, clear persisted data, and reload
export const logout = async () => {
  // Clear Featurebase identity before logout
  clearFeaturebaseIdentity();

  // Clear mobile data (auth tokens, cache) for native apps
  await clearMobileData();

  try {
    const { error} = await supabase.auth.signOut();
    if (error) {
      console.warn('[Logout] SignOut error (session may be expired):', error.message);
    }
  } catch (error) {
    console.warn('[Logout] SignOut failed:', error);
  }

  // Preserve theme preference across logout (not user-specific)
  let savedTheme = null;
  try {
    const uiData = localStorage.getItem('promptnote-ui');
    if (uiData) {
      const parsed = JSON.parse(uiData);
      savedTheme = parsed?.theme;
    }
  } catch {
    // Ignore parse errors
  }

  // Clear all user-specific persisted data from localStorage
  // Legend-State uses __m (metadata) and __p (pending changes) suffixes
  const userDataKeys = [
    'promptnote-notes',
    'promptnote-snippets',
    'promptnote-pin',
    'promptnote-shared-tag-paths',
    'promptnote-shared-with-me',
    'promptnote-ui',
  ];
  userDataKeys.forEach(key => {
    localStorage.removeItem(key);
    localStorage.removeItem(`${key}__m`);
    localStorage.removeItem(`${key}__p`);
  });
  console.log('[Logout] Cleared persisted user data from localStorage');

  // Restore theme preference so next user (or login screen) keeps the theme
  if (savedTheme) {
    try {
      localStorage.setItem('promptnote-ui', JSON.stringify({ theme: savedTheme }));
    } catch {
      // Ignore
    }
  }

  // Force a full page reload to reset all in-memory observable state
  console.log('[Logout] Reloading page...');
  window.location.href = '/';
};

// Get auth headers (for API calls if needed)
export const getAuthHeaders = () => {
  const session = auth$.session.get();
  return session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {};
};

// ============================================================================
// ANONYMOUS USER UPGRADE FUNCTIONS
// ============================================================================

/**
 * Upgrade anonymous account to permanent account with email and password
 * User stays logged in immediately, email verification happens in background
 *
 * @param {string} email - Email address
 * @param {string} username - Username for user_metadata
 * @param {string} password - Password to set
 * @returns {Promise} - Result of updateUser call
 */
export const upgradeAnonymousAccount = async (email, username, password) => {
  const isAnonymous = auth$.isAnonymous.get();
  if (!isAnonymous) {
    throw new Error('User is not anonymous. Use regular login instead.');
  }

  console.log('[Auth] Upgrading anonymous account to permanent account:', email);

  // Update user with email and password - user stays logged in
  // Email verification will be sent but user can continue using the app
  const { data, error } = await supabase.auth.updateUser({
    email,
    password,
    data: { username, app_id: 'promptnote' },
  });

  if (error) {
    console.error('[Auth] Error upgrading account:', error);
    throw error;
  }

  console.log('[Auth] Account upgraded successfully! User can continue using the app.');

  trackEvent('account_upgraded', { method: 'email' });
  setOnceUserProperties({ upgraded_at: new Date().toISOString() });

  return data;
};

/**
 * Upgrade anonymous user to permanent account with email/password
 * Step 1: Link email to anonymous account
 * Step 2: User verifies email (clicks link or enters OTP)
 * Step 3: Set password after verification
 *
 * @param {string} email - Email address to link
 * @param {string} username - Optional username for user_metadata
 * @returns {Promise} - Result of updateUser call
 */
export const linkEmail = async (email, username) => {
  const isAnonymous = auth$.isAnonymous.get();
  if (!isAnonymous) {
    throw new Error('User is not anonymous. Use regular login instead.');
  }

  console.log('[Auth] Linking email to anonymous account:', email);

  // Update user with email - this will send a verification email
  const { data, error } = await supabase.auth.updateUser({
    email,
    data: username ? { username } : undefined,
  });

  if (error) {
    console.error('[Auth] Error linking email:', error);
    throw error;
  }

  console.log('[Auth] Email link initiated. User must verify email before setting password.');
  return data;
};

/**
 * Set password for anonymous user after email verification
 * This should be called AFTER the user has verified their email
 *
 * @param {string} password - Password to set
 * @returns {Promise} - Result of updateUser call
 */
export const setPasswordAfterEmailVerification = async (password) => {
  console.log('[Auth] Setting password after email verification...');

  const { data, error } = await supabase.auth.updateUser({
    password,
  });

  if (error) {
    console.error('[Auth] Error setting password:', error);
    throw error;
  }

  console.log('[Auth] Password set successfully. Anonymous account upgraded!');
  return data;
};

/**
 * Upgrade anonymous user to permanent account with OAuth (Google/GitHub)
 * This uses Supabase's linkIdentity feature to link OAuth provider to anonymous account
 *
 * @param {string} provider - OAuth provider ('google', 'github', or 'apple')
 * @returns {Promise} - Result of linkIdentity call
 */
export const linkOAuthIdentity = async (provider) => {
  const isAnonymous = auth$.isAnonymous.get();
  if (!isAnonymous) {
    throw new Error('User is not anonymous. Use regular OAuth login instead.');
  }

  console.log('[Auth] Linking OAuth identity to anonymous account:', provider);

  if (Capacitor.isNativePlatform()) {
    const { Browser } = await import('@capacitor/browser');
    const { data, error } = await supabase.auth.linkIdentity({
      provider,
      options: {
        redirectTo: NATIVE_REDIRECT,
        skipBrowserRedirect: true,
      },
    });
    if (error) {
      console.error('[Auth] Error linking OAuth identity:', error);
      throw error;
    }
    if (data?.url) {
      await Browser.open({ url: data.url });
    }
    console.log('[Auth] OAuth identity link initiated via native browser');
    return data;
  }

  const { data, error } = await supabase.auth.linkIdentity({
    provider,
    options: {
      redirectTo: window.location.origin,
    },
  });

  if (error) {
    console.error('[Auth] Error linking OAuth identity:', error);
    throw error;
  }

  console.log('[Auth] OAuth identity link initiated. User will be redirected...');
  return data;
};

